import { type FC, type ReactNode, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import StyleIcon from '@mui/icons-material/Style';
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import type { LabelGroup } from '../../types';

export interface LabelGroupRowProps {
  group: LabelGroup;
  labelCount: number;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onRename: (name: string) => void;
  onAddLabel: () => void;
  onDelete: () => void;
  /** The group's label rows, rendered under a guide rail when expanded. */
  children?: ReactNode;
}

/**
 * A collapsible group header (GDN-2019) — caret, group glyph, inline-editable
 * name + member count, and a `•••` menu (rename / add label / delete). Child
 * label rows render beneath a left guide rail, mirroring Linear's tree.
 */
export const LabelGroupRow: FC<LabelGroupRowProps> = ({
  group,
  labelCount,
  collapsed,
  onToggleCollapse,
  onRename,
  onAddLabel,
  onDelete,
  children,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(group.name);

  const closeMenu = () => setAnchorEl(null);

  const startEdit = () => {
    setDraft(group.name);
    setEditing(true);
    closeMenu();
  };

  const commitEdit = () => {
    const next = draft.trim();
    if (next && next !== group.name) onRename(next);
    setEditing(false);
  };

  return (
    <Box sx={{ mb: 0.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          py: 0.5,
          '&:hover .group-row-actions': { opacity: 1 },
        }}
      >
        <IconButton
          size="small"
          aria-label={collapsed ? 'Expand group' : 'Collapse group'}
          onClick={onToggleCollapse}
        >
          {collapsed ? (
            <ChevronRightIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>

        <StyleIcon sx={{ fontSize: 18, color: 'text.disabled' }} />

        {editing ? (
          <TextField
            autoFocus
            size="small"
            variant="standard"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => {
              if (e.key === 'Enter') commitEdit();
              if (e.key === 'Escape') setEditing(false);
            }}
            sx={{ flex: 1 }}
          />
        ) : (
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600 }}
            onDoubleClick={startEdit}
          >
            {group.name}
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary">
          {labelCount}
        </Typography>

        <Box sx={{ flex: 1 }} />

        <IconButton
          size="small"
          className="group-row-actions"
          aria-label={`Actions for group ${group.name}`}
          onClick={e => setAnchorEl(e.currentTarget)}
          sx={{ opacity: 0, transition: 'opacity 120ms' }}
        >
          <MoreHorizIcon fontSize="small" />
        </IconButton>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
          <MenuItem onClick={startEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit group name</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              onAddLabel();
              closeMenu();
            }}
          >
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add label to group</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              onDelete();
              closeMenu();
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete group</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {!collapsed && children && (
        <Box
          sx={{
            ml: 2,
            pl: 1.5,
            borderLeft: '1px solid',
            borderColor: 'divider',
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
};
